<template>
    <div class="table-sticky-header">
        <div class="b-table-content-wrapper">
            <div class="b-table-content">
                <div class="b-table-overflow">
                    <div class="b-table-sticky-header">
                        <b-table
                            ref="table"
                            :items="filteredItems"
                            :fields="fields"
                            class="table-styled"
                            small
                            sort-icon-left
                            :tbody-tr-class="$_rowClass"
                            @row-clicked="$_onRowClicked"
                            @sort-changed="$_onSortChanged"
                            :thead-class="showFilter ? 'has-filter' : ''"
                            :sort-compare="customSort"
                        >
                            <!-- Custom header cell with filter input field -->
                            <template #head()="data">
                                {{ data.field.label }}
                                <div>
                                    <input
                                        v-if="
                                            showFilter && data.field.filterable
                                        "
                                        v-model="filters[data.field.key]"
                                        @input="$_onInputFilter"
                                        type="search"
                                        class="input-filter"
                                    />
                                </div>
                            </template>
                            <!-- Custom header cell for 'selected' -->
                            <template #head(selected)>
                                <x-checkbox
                                    :id="`${group.order}_exportAll`"
                                    :name="`${group.order}_exportAll`"
                                    :value="hasTableSelection"
                                    @change="$_onSelectAllRows"
                                    :indeterminate="
                                        hasInderminateTableSelection
                                    "
                                >
                                </x-checkbox>
                            </template>
                            <!-- Custom field for 'selected' -->
                            <template v-slot:cell(selected)="row">
                                <x-checkbox
                                    :id="`exportRow_${group.order}_${row.index}`"
                                    :name="`exportRow_${group.order}_${row.index}`"
                                    :value="tableSelections[row.index]"
                                    @change="$_onSelectRow(row.index, $event)"
                                >
                                </x-checkbox>
                            </template>
                            <!-- Custom field for 'parcel' -->
                            <template v-slot:cell(parcel)="row">
                                <b-link
                                    v-if="row.item.parcel"
                                    id="interactive-report"
                                    :title="`${$t('stuf.toon')} ${$t(
                                        'cadastralInformationTitle'
                                    ).toLowerCase()}`"
                                    @click="$emit('openBrkDetails', row.value)"
                                >
                                    <div class="report-icon"></div>
                                </b-link>
                            </template>
                            <!-- Custom field for 'editable' -->
                            <template v-slot:cell(editable)="row">
                                <b-link
                                    id="row_edit"
                                    @click.stop="$_onEditFeature(row.index)"
                                >
                                    <div
                                        class="edit-icon delete-edit-icon"
                                    ></div>
                                </b-link>
                            </template>
                            <!-- Custom field for 'relations' -->
                            <template v-slot:cell(relations)="row">
                                <!-- Dropdown button for relations -->
                                <relations-toolbar-dropdown
                                    :relations="relations"
                                    @openRelation="
                                        $_openRelation($event, row.index)
                                    "
                                />
                            </template>
                            <!-- Default data cell scoped slot -->
                            <template #cell()="row">
                                <feature-info-item
                                    :feature="row.item"
                                    :property="row.item[row.field.key]"
                                />
                            </template>
                        </b-table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

    <script>
    import { mapGetters, mapActions } from 'vuex';
    import {
        CONTENT_TYPE_ID,
        RELATION_TYPES,
        INTERFACE_PANEL_TRIGGERS,
        RELATIONSHIP_TYPES,
        NEDGLOBE_SERVICES
    } from '../../../../constants';
    import { isSmallDevice } from '../../../../services/utilities/devices';
    import FeatureInfoItem from './FeatureInfoItem.vue';
    import RelationsToolbarDropDown from '../../information/toolbars/RelationsToolbarDropDown.vue';

    export default {
        props: {
            group: { type: Object, required: true },
            showFilter: { type: Boolean, required: false, default: false },
            selectable: { type: Boolean, required: false, default: false },
            selectedObject: { type: Object, required: false }
        },
        components: {
            'feature-info-item': FeatureInfoItem,
            'relations-toolbar-dropdown': RelationsToolbarDropDown
        },
        data() {
            return {
                tableSelections: [],
                filters: [],
                localObjects: []
            };
        },
        watch: {
            group: {
                handler: function (newVal) {
                    // Fill localObjects first
                    // getAutoOpenRelations needs this data to be filled
                    this.localObjects = [...newVal.objects];
                    // New group data

                    // Open relations automatically:
                    // 1 - Only if state.autoOpenRelations has been set
                    //     to true by calling the CLEAR_SELECTIONS mutation.
                    // 2 - Only if tableview is present
                    //     Tableview is not present on small devices,
                    //     except in web, when 'Show tableview on small devices' is checked
                    const hasTableView = !isSmallDevice() || this.getShowTableViewSmallDevices;
                    if (this.getAutoOpenRelations && hasTableView) {
                        // Set state.autoOpenRelations to false so we only do this once.
                        this.$store.commit('SET_AUTO_OPEN_RELATIONS', false);
                        this.$_autoOpenRelations();
                    }
                },
                immediate: true
            },
            featureData: {
                handler: function (newVal) {
                    this.tableSelections = newVal.map((item) => false);
                },
                immediate: true
            },
            tableSelections(newVal) {
                const exportData = this.featureData
                    .filter((item, index) => newVal[index])
                    .map((item) => {
                        const returnItem = { ...item };
                        delete returnItem.geometry;
                        return returnItem;
                    });
                this.$emit('setExport', exportData);

                const selectedFeaturesData = this.localObjects
                    .filter((item, index) => newVal[index])
                    .map((item) => {
                        return item.feature;
                    });
                this.$emit('setSelectedFeatures', selectedFeaturesData);
            },
            selectedObject: {
                handler: function (newVal) {
                    this.group.objects.forEach((item) => {
                        this.$set(item.info, 'isActive', false);
                    });

                    if (newVal) {
                        const item = this.group.objects.find(
                            (row) => row.id === newVal.id
                        );
                        if (item) {
                            this.$set(item.info, 'isActive', true);
                        }
                    }
                },
                immediate: true
            },
            showFilter: {
                handler: function () {
                    this.filters = [];
                },
                immediate: true
            }
        },
        computed: {
            ...mapGetters([
                'getLayerRelations',
                'getSelectionData',
                'getAutoOpenRelations',
                'getShowTableViewSmallDevices',
                'getLayerByOriginalId'
            ]),
            fields() {
                const fields = this.group.informationAttributes
                    .filter(({ displayInTable }) => displayInTable)
                    .map((item) => ({
                        key: item.attribute,
                        label: item.alias
                            ? item.alias.toUpperCase()
                            : item.attribute.toUpperCase(),
                        sortable: true,
                        filterable: true
                    }));

                // first column with checkbox
                const selected = [
                    {
                        key: 'selected',
                        label: '',
                        tdClass: 'checkboxcell',
                        thClass: 'checkboxcell'
                    }
                ];

                // sticky column at the end with BRK info
                const parcel = [
                    {
                        key: 'parcel',
                        label: '',
                        tdClass: 'sticky-col'
                    }
                ];

                // sticky column at the end for editing
                const editable = [
                    {
                        key: 'editable',
                        label: '',
                        tdClass: 'sticky-col',
                        tdAttr: () => ({
                            title: this.$t('labelButtonModify')
                        })
                    }
                ];

                // sticky column at the end for relations
                const relations = [
                    {
                        key: 'relations',
                        label: '',
                        tdClass: 'sticky-col',
                        tdAttr: () => ({
                            title: this.relationsTitle
                        })
                    }
                ];

                return [
                    ...((this.selectable && selected) || []),
                    ...fields,
                    ...((this.hasParcel && parcel) || []),
                    ...((this.group.editable && editable) || []),
                    ...((this.hasRelations && relations) || [])
                ];
            },
            featureData() {
                return this.localObjects.map(({ info }) => info);
            },
            filteredItems() {
                const filtered = this.featureData.filter((item) => {
                    return Object.keys(this.filters).every((key) =>
                        String(item[key].displayValue)
                            .toLowerCase()
                            .includes(this.filters[key].toLowerCase())
                    );
                });
                return filtered.length > 0 ? filtered : [];
            },
            hasTableSelection() {
                return !!this.tableSelections.find((feature) => feature === true);
            },
            hasInderminateTableSelection() {
                return (
                    this.hasTableSelection &&
                    this.tableSelections.some((feature) => feature === false)
                );
            },
            relations() {
                return this.getLayerRelations(
                    this.group.layer.id
                );
            },
            hasRelations() {
                return this.relations?.length > 0;
            },
            relationsTitle() {
                return this.relations.length === 1
                    ? `${this.$t('relations.showRelated')} ${
                          this.relations[0].name
                      }`
                    : this.$t('relations.showRelations');
            },
            hasParcel() {
                return !!this.featureData.find(({ parcel }) => parcel);
            },
            hasDetailInfo() {
                return this.selectedObject?.info.length > 0;
            }
        },
        methods: {
            ...mapActions([
                'findDataRelation',
                'findSpatialRelation',
                'findThemeLayer2TableRelation',
                'clearTabsRight',
                'setTabToFocus',
                'addLayerTab'
            ]),
            $_onSortChanged() {
                this.localObjects.forEach((object, index) => {
                    const index1 = this.$refs.table.sortedItems.findIndex(
                        (bl) => bl.nedGlobeRowId === object.info.nedGlobeRowId
                    );
                    object.index = index1;
                });
                this.localObjects.sort((a, b) => a.index - b.index);
                this.$emit('sortObjects', {
                    sortedObjects: this.localObjects,
                    layerId: this.group.layer.id
                });
            },
            $_onInputFilter() {
                // Reset all checkboxes when filter field is filled
                this.tableSelections = this.featureData.map((item) => false);
            },
            $_onRowClicked(record, featureIndex) {
                const sortedObjects = [...this.group.objects].sort(
                    (a, b) => a.index - b.index
                );

                const newIndex = sortedObjects.findIndex(
                    (bl) => bl.info.nedGlobeRowId === record.nedGlobeRowId
                );

                if (
                    newIndex > -1 &&
                    this.selectedObject?.id &&
                    this.selectedObject.id === sortedObjects[newIndex].id
                ) {
                    this.$store.dispatch('zoomToFeature', {
                        mapName: 'main',
                        feature: [sortedObjects[newIndex].feature]
                    });
                    // only show info panel if it has attributes to show
                    if (this.hasDetailInfo) {
                        this.$store.dispatch('togglePanel', {
                            panelName: INTERFACE_PANEL_TRIGGERS.OBJECT_DETAILS,
                            visible: true
                        });
                    }
                } else if (newIndex >= 0) {
                    this.$emit('openObject', {
                        group: this.group,
                        index: newIndex
                    });
                }
            },
            $_onSelectRow(featureIndex, val) {
                const values = [...this.tableSelections];
                values[featureIndex] = val;
                this.tableSelections = values;
            },
            $_onSelectAllRows(val) {
                this.tableSelections = this.tableSelections.map((item) => val);
            },
            $_onEditFeature(featureIndex) {
                if (featureIndex >= 0) {
                    const feature = this.localObjects[featureIndex].feature;
                    const layer = this.group.layer;
                    this.$store.commit('RESET_MAP_FEATURES', { mapName: 'main' });
                    let panelName = '';
                    if (
                        layer.extended.serviceType === 'WFS' &&
                        layer.extended.isTransactional
                    ) {
                        panelName = 'WfsTEdit';
                    } else {
                        panelName =
                            layer.extended.contentTypeId ===
                            CONTENT_TYPE_ID.FEEDBACK
                                ? 'feedbackEdit'
                                : 'measureEdit';
                    }
                    this.$store.dispatch('togglePanel', {
                        panelName,
                        visible: true,
                        data: { layer, feature }
                    });
                }
            },

            async $_openRelation(relation, featureIndex) {
                // If featureindex > -1 a relation is opened from a tab.
                // We delete all data to the right of this tab.
                // We give tabToFocus a value so tabs.vue knows to set focus to the last tab.
                if (featureIndex > -1) {
                    this.clearTabsRight({ title: this.group.title });
                    const relationTabName = this.$_getRelationTabName(relation);
                    // Delete existing tabs with the same name
                    this.$store.commit('CLEAR_SELECTION_DATA', {
                        title: relationTabName
                    });

                    this.addLayerTab({
                        title: relationTabName
                    });

                    this.setTabToFocus({ title: relationTabName });
                } else {
                    featureIndex = 0;
                }

                if (relation.type === RELATIONSHIP_TYPES.ThemeLayer2Table) {
                    this.findThemeLayer2TableRelation({
                        relation,
                        objectData: [this.$_getObjectData(relation, featureIndex)],
                        features: [this.localObjects[featureIndex].feature],
                        computedStyles: this.group.layer.extended.computedStyles
                    });
                } else if (relation.relationType === RELATION_TYPES.Normal) {
                    this.findDataRelation({
                        relation,
                        filters: this.$_getFilterData(relation, featureIndex),
                        tabName: this.$_getRelationTabName(relation)
                    });
                } else {
                    this.findSpatialRelation({
                        relation,
                        features: [this.localObjects[featureIndex].feature],
                        tabName: this.$_getRelationTabName(relation)
                    });
                }
            },

            $_getObjectData(relation, featureIndex) {
                const featureData = this.featureData[featureIndex];
                return relation.settings.items.map((relationItem) => ({
                    caseSensitive: relationItem.caseSensitive,
                    sourceFieldName: relationItem.sourceFieldName,
                    sourceFieldValue:
                        featureData[relationItem.sourceFieldName]?.value?.toString()
                }));
            },
            $_getFilterData(relation, featureIndex) {
                const featureData = this.featureData[featureIndex];
                return relation.settings.items
                    .filter(
                        (relationItem) =>
                            featureData[relationItem.sourceFieldName]?.value
                    )
                    .map((relationItem) => ({
                        caseSensitive: relationItem.caseSensitive,
                        filterAttribute: relationItem.destinationFieldName,
                        operator: '=',
                        value: featureData[relationItem.sourceFieldName]?.value,
                        visible: true,
                        dataType: `xsd:${relationItem.dataType}`
                    }));
            },
            $_getRelationTabName(relation) {
                const layer = this.getLayerByOriginalId(
                    relation.targetThemeLayerId
                );
                if (layer) {
                    return layer.data.title;
                } else {
                    return relation.name;
                }
            },
            $_autoOpenRelations() {
                // Opens table relation tabs automatically when
                // There are relations
                // There is exactly 1 layer with a selection result
                // There is exactly 1 selection result
                // The data source is NedGlobe Services
                // And auto open is enabled
                if (
                    this.hasRelations &&
                    this.getSelectionData.results.length === 1 &&
                    this.group.count === 1 &&
                    this.group.layer.data.source.name === NEDGLOBE_SERVICES &&
                    this.group.layer.data.autoOpenAllRelationsTabs
                ) {
                    this.relations
                        .filter(
                            ({ type }) =>
                                type === RELATIONSHIP_TYPES.ThemeLayer2Table
                        )
                        .sort((a, b) => a.order - b.order)
                        .forEach((relation) => {
                            this.$_openRelation(relation, -1);
                        });
                }
            },
            $_rowClass(item, type) {
                if (type === 'row-top') {
                    return this.showFilter ? '' : 'd-none';
                }
                if (!item || type !== 'row') {
                    return '';
                }
                return {
                    active: item.isActive
                };
            },
            // When using b-table with sortable columns that contain objects as values,
            // sorting can behave unexpectedly because the default sorting mechanism does not handle objects correctly.
            // To fix this issue, use a custom sort-compare function.
            customSort(a, b, key) {
                // Treat null/undefined as empty string
                const valA = a[key].displayValue || '';
                const valB = b[key].displayValue || '';

                // Empty values go to the top
                if (valA === '' && valB !== '') {
                    return -1;
                }
                if (valA !== '' && valB === '') {
                    return 1;
                }
                if (valA < valB) {
                    return -1;
                }
                if (valA > valB) {
                    return 1;
                }
                return 0;
            }
        }
    };
</script>

<style lang="scss" scoped>
.overview-message {
    white-space: pre-line;
    padding-bottom: 10px;
}

.overview-message.has-results {
    padding-top: 10px;
}

/* Refresh icon in table */
.rotate-white-icon.table-icon {
    width: 20px;
    height: 20px;
    margin-top: 5px;
    margin-right: 10px;
}

.rotate-white-icon.table-icon.inactive {
    background: url('~@/assets/icons/ic_fluent_arrow_clockwise_20_inactive.svg')
        no-repeat center center;
    background-size: 22px;
}

.rotate-white-icon.table-icon.inactive:hover {
    cursor: unset;
}

.edit-icon {
    background-color: $blue-05;
}
</style>
